import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Plus, Loader2, Trash2, Search, ShoppingBag, Save } from "lucide-react";
import { pedidosApi, articulosConfigApi, ArticuloConfig, pasarelasApi, deliveryZonasApi } from "../../lib/api";

interface LineaForm {
  articuloId?: number;
  codArticulo?: number;
  nombreArticulo: string;
  fotoUrl?: string;
  cantidad: number;
  precioUnitario: number;
  observaciones?: string;
}

export function NuevoPedidoModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();

  // Datos cliente
  const [nombreCliente, setNombreCliente] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");

  // Entrega
  const [tipoEntrega, setTipoEntrega] = useState<"delivery" | "retiro" | "mesa">("delivery");
  const [direccionEntrega, setDireccionEntrega] = useState("");
  const [referenciaDireccion, setReferenciaDireccion] = useState("");
  const [deliveryZonaId, setDeliveryZonaId] = useState<number | undefined>();
  const [costoEnvio, setCostoEnvio] = useState(0);

  // Pago
  const [pasarelaId, setPasarelaId] = useState<number | undefined>();

  // Canal
  const [canalOrigen, setCanalOrigen] = useState<"admin" | "telefono" | "whatsapp">("telefono");

  // Items
  const [lineas, setLineas] = useState<LineaForm[]>([]);
  const [busqueda, setBusqueda] = useState("");
  const [debBusqueda, setDebBusqueda] = useState("");
  const [showBuscar, setShowBuscar] = useState(false);

  // Notas
  const [notasCliente, setNotasCliente] = useState("");
  const [notasInternas, setNotasInternas] = useState("");

  // Error
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebBusqueda(busqueda), 250);
    return () => clearTimeout(t);
  }, [busqueda]);

  const articulos = useQuery({
    queryKey: ["articulos-config", "buscar-nuevo-pedido", debBusqueda],
    queryFn: () => articulosConfigApi.list({
      busqueda: debBusqueda || undefined,
      limit: 30,
    }),
    enabled: showBuscar,
  });

  const pasarelas = useQuery({
    queryKey: ["pasarelas"],
    queryFn: () => pasarelasApi.list(),
  });

  const zonas = useQuery({
    queryKey: ["delivery-zonas", "activas"],
    queryFn: () => deliveryZonasApi.list(true),
  });

  const subtotal = lineas.reduce((s, l) => s + l.cantidad * l.precioUnitario, 0);
  const total = subtotal + costoEnvio;

  function agregarLinea(a: ArticuloConfig) {
    const existente = lineas.find((l) => l.articuloId === a.articuloId);
    if (existente) {
      setLineas(lineas.map((l) =>
        l.articuloId === a.articuloId ? { ...l, cantidad: l.cantidad + 1 } : l
      ));
    } else {
      setLineas([...lineas, {
        articuloId: a.articuloId,
        codArticulo: a.codArticulo,
        nombreArticulo: a.nombre,
        fotoUrl: a.fotoUrl || a.fotoIcgUrl || undefined,
        cantidad: 1,
        precioUnitario: Number(a.precioDeliveryUsd || a.precioUsd) || 0,
      }]);
    }
    setShowBuscar(false);
    setBusqueda("");
  }

  function quitarLinea(idx: number) {
    setLineas(lineas.filter((_, i) => i !== idx));
  }

  function actualizarLinea(idx: number, patch: Partial<LineaForm>) {
    setLineas(lineas.map((l, i) => i === idx ? { ...l, ...patch } : l));
  }

  const crearMut = useMutation({
    mutationFn: () => pedidosApi.crear({
      nombreCliente, telefono: telefono || undefined, email: email || undefined,
      tipoEntrega,
      direccionEntrega: tipoEntrega === "delivery" ? direccionEntrega : undefined,
      referenciaDireccion: tipoEntrega === "delivery" ? referenciaDireccion : undefined,
      deliveryZonaId: tipoEntrega === "delivery" ? deliveryZonaId : undefined,
      pasarelaId,
      costoEnvio,
      canalOrigen,
      notasCliente: notasCliente || undefined,
      notasInternas: notasInternas || undefined,
      lineas: lineas.map((l) => ({
        articuloId: l.articuloId, codArticulo: l.codArticulo,
        nombreArticulo: l.nombreArticulo, fotoUrl: l.fotoUrl,
        cantidad: l.cantidad, precioUnitario: l.precioUnitario,
        observaciones: l.observaciones,
      })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pedidos"] });
      onClose();
    },
    onError: (e: any) => setError(e.message || "Error creando pedido"),
  });

  function guardar() {
    setError(null);
    if (!nombreCliente.trim()) return setError("Ingresa el nombre del cliente");
    if (lineas.length === 0) return setError("Agrega al menos un artículo");
    if (tipoEntrega === "delivery" && !direccionEntrega.trim()) return setError("Falta la dirección de entrega");
    crearMut.mutate();
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
          <h2 className="font-display font-bold text-ink-800 inline-flex items-center gap-2">
            <ShoppingBag className="size-5 text-brand-600" /> Nuevo pedido manual
          </h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700">
            <X className="size-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Columna izquierda */}
          <div className="space-y-3">
            {error && (
              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Nombre del cliente *</label>
              <input type="text" value={nombreCliente} onChange={(e) => setNombreCliente(e.target.value)}
                className="input w-full" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-1">Teléfono</label>
                <input type="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)}
                  placeholder="04141234567" className="input w-full" />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-1">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="input w-full" />
              </div>
            </div>

            <div className="flex gap-2 mt-2">
              {(["delivery", "retiro", "mesa"] as const).map((t) => (
                <button key={t} onClick={() => setTipoEntrega(t)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${tipoEntrega === t ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}>
                  {t === "delivery" ? "🚚 Delivery" : t === "retiro" ? "🏪 Retiro" : "🍽️ Mesa"}
                </button>
              ))}
            </div>

            {tipoEntrega === "delivery" && (
              <>
                <div>
                  <label className="block text-xs font-medium text-ink-600 mb-1">Dirección *</label>
                  <textarea rows={2} value={direccionEntrega} onChange={(e) => setDireccionEntrega(e.target.value)}
                    placeholder="Calle, número, urbanización..." className="input w-full" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink-600 mb-1">Referencia</label>
                  <input type="text" value={referenciaDireccion} onChange={(e) => setReferenciaDireccion(e.target.value)}
                    placeholder="Punto de referencia, piso/apto" className="input w-full" />
                </div>
                {zonas.data && zonas.data.length > 0 ? (
                  <div>
                    <label className="block text-xs font-medium text-ink-600 mb-1">Zona de delivery</label>
                    <select
                      value={deliveryZonaId || ""}
                      onChange={(e) => {
                        const zid = e.target.value ? Number(e.target.value) : undefined;
                        setDeliveryZonaId(zid);
                        const z = zonas.data?.find((z) => z.id === zid);
                        setCostoEnvio(z ? Number(z.costoEnvio) : 0);
                      }}
                      className="input w-full"
                    >
                      <option value="">— Seleccionar zona —</option>
                      {zonas.data.map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.nombre} — ${Number(z.costoEnvio).toFixed(2)}
                          {z.tiempoEstimadoMin ? ` (~${z.tiempoEstimadoMin} min)` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-ink-600 mb-1">Costo de envío USD</label>
                    <input type="number" step="0.01" value={costoEnvio} onChange={(e) => setCostoEnvio(Number(e.target.value))}
                      className="input w-full" />
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-1">Pasarela</label>
                <select value={pasarelaId || ""} onChange={(e) => setPasarelaId(e.target.value ? Number(e.target.value) : undefined)}
                  className="input w-full">
                  <option value="">— Sin elegir —</option>
                  {pasarelas.data?.filter((p) => p.activa).map((p) => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-1">Canal</label>
                <select value={canalOrigen} onChange={(e) => setCanalOrigen(e.target.value as any)}
                  className="input w-full">
                  <option value="telefono">📞 Teléfono</option>
                  <option value="whatsapp">💬 WhatsApp</option>
                  <option value="admin">⚙️ Admin</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Notas del cliente</label>
              <textarea rows={2} value={notasCliente} onChange={(e) => setNotasCliente(e.target.value)}
                className="input w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Notas internas</label>
              <textarea rows={2} value={notasInternas} onChange={(e) => setNotasInternas(e.target.value)}
                className="input w-full" />
            </div>
          </div>

          {/* Columna derecha: items */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-ink-800">Artículos ({lineas.length})</h3>
              <button onClick={() => setShowBuscar(true)}
                className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium inline-flex items-center gap-1">
                <Plus className="size-3.5" /> Agregar artículo
              </button>
            </div>

            {lineas.length === 0 ? (
              <div className="text-sm text-ink-400 text-center py-8 border border-dashed border-ink-200 rounded-lg">
                Sin artículos. Haz click en "Agregar artículo".
              </div>
            ) : (
              <div className="divide-y divide-ink-100 border border-ink-200 rounded-lg">
                {lineas.map((l, idx) => (
                  <div key={idx} className="p-2 flex items-center gap-2">
                    {l.fotoUrl ? (
                      <img src={l.fotoUrl} className="size-8 rounded object-cover shrink-0" alt="" />
                    ) : (
                      <div className="size-8 rounded bg-ink-100 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink-800 truncate">{l.nombreArticulo}</div>
                      <input type="text" value={l.observaciones || ""}
                        onChange={(e) => actualizarLinea(idx, { observaciones: e.target.value })}
                        placeholder="Observación..."
                        className="text-xs w-full bg-transparent border-none p-0 mt-0.5 text-ink-500" />
                    </div>
                    <input type="number" step="0.5" min="0.5" value={l.cantidad}
                      onChange={(e) => actualizarLinea(idx, { cantidad: Number(e.target.value) })}
                      className="w-14 input text-sm" />
                    <input type="number" step="0.01" value={l.precioUnitario}
                      onChange={(e) => actualizarLinea(idx, { precioUnitario: Number(e.target.value) })}
                      className="w-20 input text-sm" />
                    <button onClick={() => quitarLinea(idx)} className="text-red-600 hover:bg-red-50 rounded p-1">
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Total */}
            <div className="bg-ink-50 p-3 rounded-lg space-y-1 text-sm">
              <div className="flex justify-between text-ink-600">
                <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
              </div>
              {tipoEntrega === "delivery" && (
                <div className="flex justify-between text-ink-600">
                  <span>Envío</span><span>${costoEnvio.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-ink-800 text-base pt-2 border-t border-ink-200">
                <span>Total</span><span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-ink-200 bg-ink-50 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={guardar} disabled={crearMut.isPending}
            className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {crearMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Crear pedido
          </button>
        </div>

        {/* Modal buscar artículos */}
        {showBuscar && (
          <div className="absolute inset-0 z-10 bg-black/40 flex items-center justify-center p-4"
            onClick={() => setShowBuscar(false)}>
            <div className="bg-white rounded-xl max-w-md w-full max-h-[70vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}>
              <div className="px-4 py-3 border-b border-ink-200 flex items-center justify-between">
                <h3 className="font-bold">Buscar artículo</h3>
                <button onClick={() => setShowBuscar(false)} className="text-ink-400 hover:text-ink-700">
                  <X className="size-5" />
                </button>
              </div>
              <div className="p-3 border-b border-ink-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-400" />
                  <input autoFocus type="text" placeholder="Buscar…"
                    value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                    className="input pl-10 w-full" />
                </div>
              </div>
              <div className="overflow-y-auto flex-1">
                {articulos.isLoading ? (
                  <div className="p-8 text-center text-ink-400"><Loader2 className="size-5 animate-spin mx-auto" /></div>
                ) : (
                  <div className="divide-y divide-ink-100">
                    {articulos.data?.items.map((a) => (
                      <button key={a.articuloId} onClick={() => agregarLinea(a)}
                        className="w-full px-3 py-2 hover:bg-brand-50/40 text-left flex items-center gap-2">
                        {a.fotoUrl || a.fotoIcgUrl ? (
                          <img src={a.fotoUrl || a.fotoIcgUrl!} className="size-9 rounded object-cover shrink-0" alt="" />
                        ) : (
                          <div className="size-9 rounded bg-ink-100 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-ink-800 truncate">{a.nombre}</div>
                          <div className="text-xs text-ink-500">{a.seccionNombre || "—"}</div>
                        </div>
                        <div className="text-sm font-mono text-ink-700">${Number(a.precioDeliveryUsd || a.precioUsd).toFixed(2)}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
